import {Button} from '@material-ui/core';
import {
  ArrowRight,
  Ballot,
  CheckCircleOutline,
  MoodBad,
  NavigateNext,
} from '@material-ui/icons';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation} from 'react-router';
import {pushRoute} from '../../routes';
import DetachedIcon from '../../shared/components/detachedIcon';
import RollerLoading from '../../shared/components/rollerLoading';
import {getUrlShareReceipt} from '../../shared/helpers/getApiFromLocation';
import {getUser} from '../auth/store/selectors';
import ReceiptDialog from './components/ReceiptDialog';
import Screen from './screen';
import {requestVotingClear} from './store/actions';
import {
  getElection,
  getElectionInfo,
  getPlateDetail,
  getPlates,
  getVoteResult,
} from './store/selectors';
import {IPlate, IPlateDetail, PlateVoteStatusEnum} from './store/types';

const ResultVoting: React.FC = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {loading} = useSelector(getElectionInfo);
  const election = useSelector(getElection);
  const resultVoting = useSelector(getVoteResult);
  const [timeDelay, setTimeDelay] = useState<number>();
  const [openReceipt, setOpenReceipt] = useState<boolean>(false);
  const currentPlate: IPlateDetail | null = useSelector(getPlateDetail);
  const plates: IPlate[] = useSelector(getPlates);
  const queryParams = new URLSearchParams(useLocation().search);
  const user = useSelector(getUser);
  const userId = user && user.id ? user.id.toString() : '';
  const locale =
    queryParams.get('locale') ||
    localStorage.getItem('SEI_POCKET_LOCALE') ||
    'pt';

  useEffect(() => {
    setOpenReceiptSession(openReceipt);
  }, [openReceipt]);

  useEffect(() => {
    if (!loading && resultVoting) {
      return redirectAfterTime();
    }
  }, [resultVoting, loading]);

  useEffect(() => {
    if (!loading && !currentPlate) {
      return redirectTo();
    }
  }, [currentPlate, loading]);

  const getNextPlateUuid = (): string | null => {
    const currentPlateId =
      currentPlate && currentPlate.plate.id ? currentPlate.plate.id : undefined;

    let cursor = plates.findIndex(plate => plate.id === currentPlateId);
    if (cursor === -1) {
      return null;
    }

    let has: IPlate | undefined;
    cursor++;

    while (plates[cursor] && has === undefined) {
      if (plates[cursor].status === PlateVoteStatusEnum.OPEN) {
        has = plates[cursor];
      }
      cursor++;
    }

    if (!has) {
      has = plates.find(p => p.status === PlateVoteStatusEnum.OPEN);
    }

    return has ? has.uuid : null;
  };

  const checkHasNext = () => {
    if (!election || !election.configs.enable_auto_advance) {
      return false;
    }

    return getNextPlateUuid();
  };

  const clearDelay = () => {
    if (timeDelay) {
      clearTimeout(timeDelay);
      setTimeDelay(undefined);
    }
  };

  const getOpenReceiptSession = (): boolean => {
    return window.sessionStorage.getItem('openReceipt') === 'true';
  };

  const setOpenReceiptSession = (open: boolean): void => {
    window.sessionStorage.setItem('openReceipt', open ? 'true' : 'false');
  };

  const redirectTo = () => {
    const openReceiptSession = getOpenReceiptSession();
    clearDelay();

    if (election && election.configs.show_receipt_view) {
      if (openReceiptSession || !document.URL.includes('/resultado-votacao')) {
        return;
      }
    }

    dispatch(requestVotingClear());

    if (!document.URL.includes('/resultado-votacao')) {
      return;
    }

    const nextUuid = checkHasNext();

    if (nextUuid) {
      dispatch(pushRoute('election', nextUuid));
    } else {
      dispatch(pushRoute('home'));
    }
  };

  const redirectAfterTime = (ms = 7000) => {
    clearDelay();

    const timer: number = (setTimeout(() => {
      redirectTo();
    }, ms) as unknown) as number;

    setTimeDelay(timer);
  };

  const closeDialog = () => {
    setOpenReceipt(false);
    redirectAfterTime(3000);
  };

  const makeUrl = (): string => {
    return resultVoting
      ? getUrlShareReceipt(resultVoting.receipt, locale, userId)
      : '#';
  };

  return (
    <Screen className="result loading screen-justify-start-padding pb-60 ">
      {/*LOADING*/}
      {loading && (
        <>
          <RollerLoading color="orange" />
          <h2 className="primary-title">{t('resultado-votacao.aguarde')}</h2>
          <p className="default-subtitle">
            {t('resultado-votacao.computando-voto')}
          </p>
        </>
      )}

      {/*SUCCESS*/}
      {!loading && resultVoting && !!resultVoting.receipt && (
        <>
          <DetachedIcon size="lg" color="green" icon={<CheckCircleOutline />} />
          <br />
          <h2 className="sucess-text">{t('resultado-votacao.sucesso')}</h2>

          <br />
          <p className="default-subtitle text-center">
            {t('resultado-votacao.contabilizado-com-sucesso')}
            <br />
            {t('resultado-votacao.aguarde-redirecionamento')}
            <br />
            <br />
            <Button
              type="button"
              color="primary"
              onClick={redirectTo}
              startIcon={<NavigateNext />}
            >
              {t('resultado-votacao.continuar')}
            </Button>
          </p>

          {election && election.configs.show_receipt_view && (
            <>
              <Button
                className={`full-width `}
                type="button"
                color="primary"
                onClick={() => setOpenReceipt(true)}
                startIcon={<Ballot />}
              >
                {t('resultado-votacao.ver-comprovante')}
              </Button>

              <ReceiptDialog
                receiptUrl={makeUrl()}
                open={openReceipt}
                onClose={closeDialog}
              />
            </>
          )}

          {election && election.configs.enable_auto_advance && (
            <>
              <br />
              <Button
                className={`full-width `}
                type="button"
                color="primary"
                onClick={() => redirectTo()}
                endIcon={<ArrowRight />}
              >
                {t('resultado-votacao.proximo')}
              </Button>
            </>
          )}
        </>
      )}

      {/*ERROR*/}
      {!loading && !resultVoting && (
        <>
          <DetachedIcon size="lg" color="red" icon={<MoodBad />} />
          <br />
          <h2 className="error-text text-center">
            {t('resultado-votacao.erro-ao-votar')}
          </h2>

          <br />
          <p className="default-subtitle text-center">
            {t('resultado-votacao.erro-ao-votar-mensagem')}
          </p>
          <br />
          <Button
            className={`full-width mb-24 `}
            type="button"
            color="primary"
            onClick={() => redirectTo()}
            variant="outlined"
          >
            {t('resultado-votacao.tentar-novamente')}
          </Button>
        </>
      )}
    </Screen>
  );
};

export default ResultVoting;



// WEBPACK FOOTER //
// ./src/features/election/ResultVoting.tsx